import React, { useEffect, useMemo, useState, useCallback } from "react";
import CustomCard from "../../components/@extended/CustomCard";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useTheme,
  Box,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
  Tooltip,
  Collapse,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewCompactIcon from "@mui/icons-material/ViewCompact";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import {
  CustomTableHead,
  CustomTableHeadCell,
} from "../../components/@extended/CustomTable";
import CustomButton from "../../components/@extended/CustomButton";
import { useDownloadVisitCsvMutation, useExportSubjectsReportMutation, useGetLibraryFormQuery, useGetSubjectListQuery } from "../../store/slices/apiSlice";
import { useDispatch, useSelector } from "react-redux";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useNavigate } from "react-router-dom";
import { setSteps } from "../../store/slices/subjectSlice";
import { openBackdropLoading, updatePagination } from "../../store/slices/applicationSlice";
import _ from "lodash";
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import DownloadAEReport from "../../components/common/DownloadAEReport";
import CustomTablePagination from "../../components/common/TablePagination";
import AnimatedBloqcubeLogo from "../../components/Logo/AnimatedBloqcubeLogo";
import { openMessage } from "../../store/slices/showMessageSlice";
import ConfirmationDialog from "../../components/common/ConfirmationDialog";
import QueryListDialog from "./QueryListDialog";

// Subject form status filter options (matching backend enum)
const FORM_STATUS_FILTERS = {
  ALL_COMPLETED: 'all_completed',
  PENDING_TO_SUBMIT: 'pending_to_submit',
  PI_VERIFICATION_PENDING: 'pi_verification_pending',
  SITE_MONITOR_VERIFICATION_PENDING: 'site_monitor_verification_pending',
};

const SubjectListCard = ({ trialId, siteId, trialSiteId, setOpenAeDownload, currentUserRoleOfTrailSite, trialSiteData, formData, viewReadOnlyForm, crfFormData }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [downloadVisitCsv] = useDownloadVisitCsvMutation()
  const [exportSubjectsReport] = useExportSubjectsReportMutation();
  
  // Search state with debounce
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Pagination state from redux
  const {page, rowsPerPage} = useSelector((state) => state.application.pagination.SUBJECT_TABLE);
  const sponsorId = useSelector((state) => state.auth.sponsorId);
  
  // Filter states - all checked by default
  const [showFilters, setShowFilters] = useState(false);
  // View toggle: true = compact (icons only with legend), false = detailed (icons with labels in cells)
  const [compactView, setCompactView] = useState(false);
  const [formStatusFilter, setFormStatusFilter] = useState({
    [FORM_STATUS_FILTERS.ALL_COMPLETED]: true,
    [FORM_STATUS_FILTERS.PENDING_TO_SUBMIT]: true,
    [FORM_STATUS_FILTERS.PI_VERIFICATION_PENDING]: true,
    [FORM_STATUS_FILTERS.SITE_MONITOR_VERIFICATION_PENDING]: true,
  });

  // Compute the formStatusFilters parameter for API (comma-separated string)
  const apiFormStatusFilters = useMemo(() => {
    const selectedFilters = Object.entries(formStatusFilter)
      .filter(([_, isChecked]) => isChecked)
      .map(([key]) => key);
    
    // If all filters are selected or none are selected, return undefined (show all)
    if (selectedFilters.length === 4 || selectedFilters.length === 0) {
      return undefined;
    }
    
    return selectedFilters.join(',');
  }, [formStatusFilter]);

  // Check if no filters are selected (show nothing)
  const showNoResults = Object.values(formStatusFilter).every(v => !v);

  // Fetch subjects with pagination and filtering
  const { data: subjectResponse, isLoading, isFetching } = useGetSubjectListQuery({
    sponsorId: sponsorId,
    trialId: trialId,
    siteId: siteId,
    page: page,
    pageSize: rowsPerPage,
    formStatusFilters: apiFormStatusFilters,
    search: debouncedSearch || undefined,
  }, {
    skip: showNoResults, // Skip API call if no filters are selected
  });

  // Handle both paginated response and legacy array response
  const subjectList = useMemo(() => {
    if (!subjectResponse) return [];
    // Check if it's a paginated response (has 'data' property)
    if (subjectResponse.data && Array.isArray(subjectResponse.data)) {
      return subjectResponse.data;
    }
    // Legacy array response
    if (Array.isArray(subjectResponse)) {
      return subjectResponse;
    }
    return [];
  }, [subjectResponse]);

  // Get total count for pagination
  const totalCount = useMemo(() => {
    if (!subjectResponse) return 0;
    if (subjectResponse.total !== undefined) {
      return subjectResponse.total;
    }
    // Legacy array response
    if (Array.isArray(subjectResponse)) {
      return subjectResponse.length;
    }
    return 0;
  }, [subjectResponse]);

  const dispatch = useDispatch();
  const [rows, setRows] = React.useState([]);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: (
      <>
        <span>Are you sure you want to download this file?</span>
      </>
    ),
    buttonLabel: 'Download',
  });
  const [exportOptionsDialog, setExportOptionsDialog] = useState({
    open: false,
    format: 'csv',
    reportType: 'all',
  });
  const [queryDialogOpen, setQueryDialogOpen] = useState(false);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
      // Reset to first page when search changes
      if (searchInput !== debouncedSearch) {
        dispatch(updatePagination({ tableType: 'SUBJECT_TABLE', page: 0, rowsPerPage }));
      }
    }, 500); // 500ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  // Handle filter change
  const handleFilterChange = useCallback((filterKey) => {
    setFormStatusFilter(prev => ({
      ...prev,
      [filterKey]: !prev[filterKey],
    }));
    // Reset pagination when filter changes
    dispatch(updatePagination({ tableType: 'SUBJECT_TABLE', page: 0, rowsPerPage }));
  }, [dispatch, rowsPerPage]);

  // Count how many filters differ from default (all checked)
  const changedFilterCount = useMemo(() => {
    return Object.values(formStatusFilter).filter(v => !v).length;
  }, [formStatusFilter]);

  const columns = useMemo(() => {
    if (formData && crfFormData) {
      const data = [
        {
          id: 'subject',
          label: 'Partcipant ID',
          minWidth: 200,
          align: 'left',
        },
      ];
      formData && formData?.steps?.forEach((col) => {
        if (!col.ignoreStatusCheck)
          data.push({
            id: col.key,
            label: col.label,
            minWidth: 70,
            align: 'center',
          });
      });
      crfFormData && crfFormData?.steps?.forEach((col) => {
        if (!col.ignoreStatusCheck)
          data.push({
            id: col.key,
            label: col.label,
            minWidth: 70,
            align: 'center',
          });
      });
      return data;
    }
    return [];
  }, [formData, crfFormData]);

  // Transform subject data for display
  useEffect(() => {
    if (subjectList && subjectList.length > 0) {
      const data = subjectList.map((subject, index) => {
        const result = _.reduce(
          subject.stepStatus,
          (acc, step) => {
            acc[step.stepKey] = step.status;
            return acc;
          },
          {}
        );
        const crfStepResult = _.reduce(
          subject.crfStatus?.stepStatus || [],
          (acc, step) => {
            acc[step.stepKey] = step.status;
            return acc;
          },
          {}
        );
        return {
          srNo: index + 1,
          subject: subject?.subjectName || subject?.participantId,
          subjectId: subject?.id,
          visitCount: subject?.visitCount,
          subjectMasterId: subject?.stepStatus?.[0]?.subjectMasterId,
          subjectNumber: subject?.subjectNumber,
          isAllFormsCompleted: subject?.isAllFormsCompleted,
          hasPendingToSubmit: subject?.hasPendingToSubmit,
          hasPIVerificationPending: subject?.hasPIVerificationPending,
          hasSiteMonitorVerificationPending: subject?.hasSiteMonitorVerificationPending,
          ...result,
          ...crfStepResult,
        };
      });
      setRows(data);
    } else {
      setRows([]);
    }
  }, [subjectList]);

  const handleNavigate = (subjectId, subjectMasterId) => {
    const step = _.filter(subjectList, (su) => {
      return su.stepStatus?.[0]?.subjectMasterId === subjectMasterId;
    });
    dispatch(setSteps(step[0]?.stepStatus));
    navigate(`/eConcent/${trialId}/trial-site/${trialSiteId}/site/${siteId}/subject/${subjectMasterId}/details`, {});
  };

  const onNewPage = (newPage, newRowsPerPage) => {
    dispatch(updatePagination({ tableType: 'SUBJECT_TABLE', page: newPage, rowsPerPage: newRowsPerPage }));
  };

  const downloadVisitData = async () => {
    setConfirmDialog({
      ...confirmDialog,
      open: false,
    });
    dispatch(openBackdropLoading({ backdropLoading: true }));

    try {
      const response = await downloadVisitCsv({
        sponsorId,
        trialId,
        siteId,
      }).unwrap();

      const csvData = response;
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      const date = now.toLocaleDateString().replace(/\//g, '-');
      const time = now.toLocaleTimeString().replace(/:/g, '-');
      const dateTime = `${date}_${time}`;
      const protocolNumber = trialSiteData?.siteTrialData?.trial?.protocolNumber || '';
      const orgName = trialSiteData?.siteTrialData?.site?.orgname || '';
      const fileTypeExtension = 'csv';
      const filename = `${protocolNumber ? protocolNumber + '_' : ''}${
        orgName ? orgName + '_' : ''
      }${dateTime}.${fileTypeExtension}`;

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      dispatch(openBackdropLoading({ backdropLoading: false }));
      dispatch(
        openMessage({
          messageSeverity: 'success',
          message: 'Document downloaded successfully!',
        })
      );
    } catch (error) {
      dispatch(openBackdropLoading({ backdropLoading: false }));
      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: 'Download Failed!',
        })
      );
      console.error('Error downloading visit data file:', error);
    }
  };

  const handleExportSubjects = async () => {
    setExportOptionsDialog((prev) => ({ ...prev, open: false }));
    dispatch(openBackdropLoading({ backdropLoading: true }));
    try {
      const blob = await exportSubjectsReport({
        sponsorId: sponsorId.toString(),
        trialId: trialId.toString(),
        siteId: siteId.toString(),
        format: exportOptionsDialog.format,
        reportType: exportOptionsDialog.reportType,
      }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      const date = now.toLocaleDateString().replace(/\//g, '-');
      const time = now.toLocaleTimeString().replace(/:/g, '-');
      const dateTime = `${date}_${time}`;
      const protocolNumber = trialSiteData?.siteTrialData?.trial?.protocolNumber || '';
      const orgName = trialSiteData?.siteTrialData?.site?.orgname || '';
      const fileTypeExtension = exportOptionsDialog.format === 'excel' ? 'xlsx' : 'csv';
      const filename = `${protocolNumber ? protocolNumber + '_' : ''}${
        orgName ? orgName + '_' : ''
      }${dateTime}.${fileTypeExtension}`;

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      dispatch(openBackdropLoading({ backdropLoading: false }));
      dispatch(
        openMessage({
          messageSeverity: 'success',
          message: 'Report downloaded successfully!',
        })
      );
    } catch (error) {
      dispatch(openBackdropLoading({ backdropLoading: false }));
      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: 'Download Failed!',
        })
      );
      console.error('Error exporting subjects report:', error);
    }
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialog({
      ...confirmDialog,
      open: false,
    });
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          height: 60,
          backgroundColor: '#fafafa',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center'}}>
          <FormControl sx={{ }} variant="outlined">
            <InputLabel htmlFor="search-subject-input">Search Subject</InputLabel>
            <OutlinedInput
              id="search-subject-input"
              type={'text'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              }
              label="Search Subject"
            />
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {(currentUserRoleOfTrailSite == 'PI' ||
            currentUserRoleOfTrailSite == 'site_monitor' ||
            currentUserRoleOfTrailSite == 'site_coordinator') &&
            formData &&
            formData?.steps?.find((_obj) => _obj.key == 'InstructionsAndGuidance') && (
              <Box>
                <CustomButton
                  variant="contained"
                  onClick={() => viewReadOnlyForm('Additional Info', 'InstructionsAndGuidance')}
                >
                  <Typography variant="subtitle1" color={theme.palette.common.white}>
                    Additional Info
                  </Typography>
                </CustomButton>
              </Box>
            )}
          {(currentUserRoleOfTrailSite === 'PI' ||
            currentUserRoleOfTrailSite === 'site_monitor' ||
            currentUserRoleOfTrailSite === 'site_coordinator' ||
            currentUserRoleOfTrailSite === 'data_manager') && (
            <CustomButton variant="contained" onClick={() => setQueryDialogOpen(true)}>
              <Typography variant="subtitle1" color={theme.palette.common.white}>
                Queries
              </Typography>
            </CustomButton>
          )}
          {(currentUserRoleOfTrailSite === 'site_monitor' || currentUserRoleOfTrailSite === 'data_manager') && (
            <CustomButton
              variant="contained"
              onClick={() =>
                setExportOptionsDialog({
                  open: true,
                  format: 'csv',
                  reportType: 'all',
                })
              }
            >
              <Typography variant="subtitle1" color={theme.palette.common.white}>
                Export
              </Typography>
            </CustomButton>
          )}
          {currentUserRoleOfTrailSite === 'site_monitor' && (
            <CustomButton variant="contained" onClick={() => setOpenAeDownload(true)}>
              <Typography variant="subtitle1" color={theme.palette.common.white}>
                AE/SAE Report
              </Typography>
            </CustomButton>
          )}
          {(currentUserRoleOfTrailSite == 'PI' || currentUserRoleOfTrailSite == 'site_coordinator') && (
            <CustomButton
              variant="contained"
              onClick={() => {
                navigate(`/eConcent/${trialId}/trial-site/${trialSiteId}/SubjectEnrollmentFormParent`);
              }}
            >
              <Typography variant="subtitle1" color={theme.palette.common.white}>
                + Screen Subject
              </Typography>
            </CustomButton>
          )}
        </Box>
      </Box>

      <CustomCard
        title={'Screened and Balanced Subjects'}
        subHeader={`Total: ${showNoResults ? 0 : totalCount}`}
        sx={{
          p: 1,
          pt: 2,
          paddingBottom: 0,
          '&:last-child': {
            paddingBottom: 0,
          },
        }}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Legend - only shown in compact view */}
            {compactView && (
              <Box sx={{ display: 'flex', alignItems: 'center', paddingRight: 2 }}>
                <CheckCircleRoundedIcon
                  sx={{
                    color: theme.palette.success.light,
                    fontSize: 20,
                    mr: 0.3,
                  }}
                />
                <Typography variant="body2" sx={{ mr: 1 }}>Completed</Typography>
                <Divider orientation="vertical" sx={{ height: 20, mx: 0.5 }} flexItem />
                <WatchLaterIcon
                  sx={{
                    color: theme.palette.primary.light,
                    fontSize: 20,
                    mr: 0.3,
                  }}
                />
                <Typography variant="body2" sx={{ mr: 1 }}>PI verification pending</Typography>
                <Divider orientation="vertical" sx={{ height: 20, mx: 0.5 }} flexItem />
                <PendingActionsRoundedIcon
                  sx={{
                    color: theme.palette.primary.light,
                    fontSize: 20,
                    mr: 0.3,
                  }}
                />
                <Typography variant="body2" sx={{ mr: 1 }}>Site Monitor Verification pending</Typography>
                <Divider orientation="vertical" sx={{ height: 20, mx: 0.5 }} flexItem />
                <WatchLaterIcon
                  sx={{
                    color: 'lightgrey',
                    fontSize: 20,
                    mr: 0.3,
                  }}
                />
                <Typography variant="body2">Pending to Submit</Typography>
              </Box>
            )}

            <Tooltip title={compactView ? "Switch to detailed view (icons with labels)" : "Switch to compact view (icons only)"}>
              <IconButton
                size="small"
                onClick={() => setCompactView(!compactView)}
                sx={{
                  color: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                {compactView ? <ViewAgendaIcon fontSize="medium" /> : <ViewCompactIcon fontSize="medium" />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Filter by form status">
            <IconButton
              size="small"
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                color: changedFilterCount > 0 ? theme.palette.primary.main : theme.palette.text.secondary,
                position: 'relative',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              <FilterListIcon fontSize="medium"/>
              {changedFilterCount > 0 && (
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    borderRadius: '50%',
                    width: 16,
                    height: 16,
                    fontSize: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {changedFilterCount}
                </Box>
              )}
            </IconButton>
          </Tooltip>
          </Box>
        }
      >
          {/* Filter Panel */}
      <Collapse in={showFilters}>
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            backgroundColor: theme.palette.grey[50],
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1, display: 'block' }}>
            Filter subjects by form status:
          </Typography>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={formStatusFilter[FORM_STATUS_FILTERS.ALL_COMPLETED]}
                  onChange={() => handleFilterChange(FORM_STATUS_FILTERS.ALL_COMPLETED)}
                />
              }
              label={
                <Tooltip title="Show subjects with all enrollment and CRF forms completed">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircleRoundedIcon sx={{ color: theme.palette.success.light, fontSize: 16 }} />
                    <Typography variant="body2">Completed</Typography>
                  </Box>
                </Tooltip>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={formStatusFilter[FORM_STATUS_FILTERS.PENDING_TO_SUBMIT]}
                  onChange={() => handleFilterChange(FORM_STATUS_FILTERS.PENDING_TO_SUBMIT)}
                />
              }
              label={
                <Tooltip title="Show subjects with forms pending to submit">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WatchLaterIcon sx={{ color: 'lightgrey', fontSize: 16 }} />
                    <Typography variant="body2">Pending to Submit</Typography>
                  </Box>
                </Tooltip>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={formStatusFilter[FORM_STATUS_FILTERS.PI_VERIFICATION_PENDING]}
                  onChange={() => handleFilterChange(FORM_STATUS_FILTERS.PI_VERIFICATION_PENDING)}
                />
              }
              label={
                <Tooltip title="Show subjects with forms pending PI verification">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WatchLaterIcon sx={{ color: theme.palette.primary.light, fontSize: 16 }} />
                    <Typography variant="body2">PI Verification Pending</Typography>
                  </Box>
                </Tooltip>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={formStatusFilter[FORM_STATUS_FILTERS.SITE_MONITOR_VERIFICATION_PENDING]}
                  onChange={() => handleFilterChange(FORM_STATUS_FILTERS.SITE_MONITOR_VERIFICATION_PENDING)}
                />
              }
              label={
                <Tooltip title="Show subjects with forms pending Site Monitor verification">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PendingActionsRoundedIcon sx={{ color: theme.palette.primary.light, fontSize: 16 }} />
                    <Typography variant="body2">Site Monitor Verification Pending</Typography>
                  </Box>
                </Tooltip>
              }
            />
          </FormGroup>
        </Box>
      </Collapse>
        <TableContainer
          sx={{
            border: `1px ${theme.palette.grey[100]} solid `,
          }}
        >
          <Table stickyHeader>
            <CustomTableHead>
              <TableRow>
                {columns &&
                  columns.map((column) => {
                    return (
                      <CustomTableHeadCell key={column.id} align={column.align} width={column.minWidth}>
                        {column.label}
                      </CustomTableHeadCell>
                    );
                  })}
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {(isLoading || isFetching) ? (
                <TableRow>
                  <TableCell colSpan={columns?.length || 8} align="center">
                    <Box
                      sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ height: 250, width: 250 }}>
                        <AnimatedBloqcubeLogo />
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (showNoResults || rows.length === 0) ? (
                <TableRow>
                  <TableCell align={'center'} colSpan={columns?.length || 8}>
                    <Typography variant="subtitle1" color="initial">
                      {showNoResults ? 'Please select at least one filter option.' : 'No Available Data.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                columns &&
                rows.map((subject, index) => {
                  return (
                    <TableRow tabIndex={-1} key={index} hover sx={{ cursor: 'pointer' }}>
                      {columns.map((column) => {
                        const value = subject[column.id];
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            onClick={() => {
                              handleNavigate(subject.subjectId, subject.subjectMasterId);
                            }}
                          >
                            {value === 'Pending' ? (
                              compactView ? (
                                <Tooltip title="Pending to Submit" placement="top" arrow>
                                  <WatchLaterIcon
                                    sx={{
                                      color: 'lightgrey',
                                      fontSize: 20,
                                    }}
                                  />
                                </Tooltip>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <WatchLaterIcon
                                    sx={{
                                      color: 'lightgrey',
                                      fontSize: 20,
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                  Pending to Submit
                                  </Typography>
                                </Box>
                              )
                            ) : value === 'Verification_Pending' ? (
                              compactView ? (
                                <Tooltip title="PI verification pending" placement="top" arrow>
                                  <WatchLaterIcon
                                    sx={{
                                      color: theme.palette.primary.light,
                                      fontSize: 20,
                                    }}
                                  />
                                </Tooltip>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <WatchLaterIcon
                                    sx={{
                                      color: theme.palette.primary.light,
                                      fontSize: 20,
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ color: theme.palette.primary.main, whiteSpace: 'nowrap' }}>
                                    PI Verification Pending
                                  </Typography>
                                </Box>
                              )
                            ) : value === 'Completed' ? (
                              compactView ? (
                                <Tooltip title="Completed" placement="top" arrow>
                                  <CheckCircleRoundedIcon
                                    sx={{
                                      color: theme.palette.success.light,
                                      fontSize: 20,
                                    }}
                                  />
                                </Tooltip>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <CheckCircleRoundedIcon
                                    sx={{
                                      color: theme.palette.success.light,
                                      fontSize: 20,
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ color: theme.palette.success.main, whiteSpace: 'nowrap' }}>
                                    Completed
                                  </Typography>
                                </Box>
                              )
                            ) : value === 'External_Verification_Pending' ? (
                              compactView ? (
                                <Tooltip title="Site Monitor Verification Pending" placement="top" arrow>
                                  <PendingActionsRoundedIcon
                                    sx={{
                                      color: theme.palette.primary.light,
                                      fontSize: 20,
                                    }}
                                  />
                                </Tooltip>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <PendingActionsRoundedIcon
                                    sx={{
                                      color: theme.palette.primary.light,
                                      fontSize: 20,
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ color: theme.palette.primary.main, whiteSpace: 'nowrap' }}>
                                   Site Monitor Verification Pending
                                  </Typography>
                                </Box>
                              )
                            ) : (
                              value
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination 
          page={page} 
          rowsPerPage={rowsPerPage} 
          count={showNoResults ? 0 : totalCount} 
          onNewPage={onNewPage} 
        />
      </CustomCard>
      <ConfirmationDialog
        open={confirmDialog.open}
        buttonLabel={confirmDialog.buttonLabel}
        message={confirmDialog.message}
        handleClose={handleConfirmDialogClose}
        handleConfirm={downloadVisitData}
      />
      <Dialog
        open={exportOptionsDialog.open}
        onClose={() => setExportOptionsDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Export Subjects</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="export-format-label">File Format</InputLabel>
              <Select
                labelId="export-format-label"
                label="File Format"
                value={exportOptionsDialog.format}
                onChange={(e) =>
                  setExportOptionsDialog((prev) => ({
                    ...prev,
                    format: e.target.value,
                  }))
                }
                size="small"
              >
                <MenuItem value="excel">Excel (.xlsx)</MenuItem>
                <MenuItem value="csv">CSV (.csv)</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Excel export will include all selected subject answers in a structured sheet. CSV export provides a flat
              file compatible with external analysis tools.
            </Typography>
          </Box>
        </DialogContent>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <CustomButton variant="outlined" onClick={() => setExportOptionsDialog((prev) => ({ ...prev, open: false }))}>
            Cancel
          </CustomButton>
          <CustomButton variant="contained" onClick={handleExportSubjects}>
            Download
          </CustomButton>
        </Box>
      </Dialog>
      <QueryListDialog
        open={queryDialogOpen}
        onClose={() => setQueryDialogOpen(false)}
        sponsorId={sponsorId}
        trialId={trialId}
        siteId={siteId}
        trialSiteId={trialSiteId}
      />
    </>
  );
};

export default SubjectListCard;
